<template>
  <div class="flex justify-between border-gray-200">
    <h1 class="font-semibold text-3xl mb-1">Orders</h1>
    <DownloadButton
      title="Download CSV"
      label="Download"
      :downloading="isDownloadingCsv"
      :disabled="orders.length === 0"
      @click="downloadCsv"
    />
  </div>

  <div class="bg-white border border-gray-200 rounded-lg mt-4">
    <OrderControls
      @update:status="onStatusChange"
      @update:date-type="onDateTypeChange"
      @update:date-range="onDateRangeChange"
      @update:clear-filters="onClearFilters"
      @update:search-term="onSearchTermChange"
    />
    <DataTable
      :is-loading="isLoading"
      :columns="paymentPlansColumns"
      :data="orders"
      :total-count="totalCount"
      :page-number="pageNumber"
      :manual-sorting="true"
      @update:page="onUpdatePage"
      @update:page-size="onUpdatePageSize"
      @update:sorting="onSortingChange"
    />
  </div>
</template>

<script setup lang="ts">
import DownloadButton from '@/components/common/DownloadButton.vue';
import { NetworkError } from '@/errors';
import type { OrderServiceGetManyRequest } from '@/models/orders';
import type { NewOrder } from '@/models/paymentPlans';
import { fetchOrders } from '@/services/orders';
import { useGeneralStore } from '@/stores/general';
import { useOrderStore } from '@/stores/orders';
import { fetchAllPaginated, generateCsvBlobUrl } from '@/utils/export.utils';
import { displayDate, displayDateTime, toRandString } from '@/utils/format.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { calcPlanValue, getBenefactor, getOutstanding, getTotalPaid } from '@/utils/order.utils';
import { deepEqual } from '@/utils/paging.utils';
import dayjs from 'dayjs';
import type { DateRange } from 'radix-vue';
import { onMounted, ref } from 'vue';
import type { ModelOrderResponse, ModelOrderResponseStateEnum } from '@layup/api-sdk';
import DataTable from '../../DataTable.vue';
import OrderControls from '../orders-controls/OrdersControls.vue';
import { paymentPlansColumns } from './OrdersTableColumns';

const orderStore = useOrderStore();
const generalStore = useGeneralStore();

const isLoading = ref(false);
const orderParams = ref<OrderServiceGetManyRequest>({
  state: 'PLACED',
  populate: ['plans', 'plans.payments', 'plans.payments.connectorData', 'plans.benefactor', 'supplier'],
});
const previousParams = ref<OrderServiceGetManyRequest>({ ...orderParams.value });
const orders = ref<ModelOrderResponse[]>([]);

const totalCount = ref(1);
const pageNumber = ref(1);

onMounted(async () => onUpdateFilter(orderParams.value, true));

const onUpdateFilter = async (params: OrderServiceGetManyRequest, skipCheck: boolean = false) => {
  const isFirstLoad = !orderStore.orders;
  if (!skipCheck) {
    if (deepEqual(params, previousParams.value)) {
      return;
    }
    previousParams.value = { ...params };
  }

  isLoading.value = true;

  if (isFirstLoad || !skipCheck) {
    orders.value = [];
  }

  try {
    params.limit = generalStore.pageSize;
    params.skip = (pageNumber.value - 1) * generalStore.pageSize;
    const response = await fetchOrders(params);
    orderStore.setOrders(response.orders ?? []);
    orders.value = response.orders ?? [];
    totalCount.value = response.totalCount || 0;
    addMetric('orders-table', { status: 'success', channel: 'merchant' });
  } catch (error) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-table', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-table', { status: 'error', channel: 'merchant' });
    }
  } finally {
    isLoading.value = false;
  }
};

const onStatusChange = (value: ModelOrderResponseStateEnum): void => {
  orderParams.value.state = value;
  onUpdateFilter(orderParams.value);
};

const onDateTypeChange = (value: string): void => {
  orderParams.value.stateUpdatedAt = value;
  onUpdateFilter(orderParams.value);
};

const onDateRangeChange = (value: DateRange): void => {
  orderParams.value.from = `${value.start?.year}-${value.start?.month}-${value.start?.day}`;
  orderParams.value.to = `${value.end?.year}-${value.end?.month}-${value.end?.day}`;
  onUpdateFilter(orderParams.value);
};

const onClearFilters = (): void => {
  pageNumber.value = 1;
  orderParams.value = { skip: 0 };
  onUpdateFilter(orderParams.value);
};

const onSearchTermChange = (value: string): void => {
  orderParams.value.search = value;
  onUpdateFilter(orderParams.value);
};

const onUpdatePage = (pageNo: number) => {
  pageNumber.value = pageNo;
  orderParams.value.skip = (pageNumber.value - 1) * generalStore.pageSize;
  onUpdateFilter(orderParams.value);
};

const onUpdatePageSize = (size: number) => {
  pageNumber.value = 1;
  generalStore.pageSize = size;
  orderParams.value.limit = size;
  onUpdateFilter(orderParams.value);
};

const isDownloadingCsv = ref(false);

const downloadCsv = async () => {
  isDownloadingCsv.value = true;
  try {
    const orders = await fetchAllPaginated<ModelOrderResponse, OrderServiceGetManyRequest>(
      fetchOrders,
      orderParams.value,
      'orders',
    );
    if (orders.length === 0) {
      console.warn('No data available for export');
      return;
    }

    const headerMap: Partial<Record<keyof NewOrder, string>> = {
      customerName: 'Customer Name',
      name: 'Order Name',
      reference: 'Reference',
      dateCreated: 'Created Date',
      expireDate: 'Expiration Date',
      planValue: 'Plan Value',
      totalReceived: 'Total Received',
      outstanding: 'Outstanding Amount',
      finalPayment: 'Final Payment',
      dateUpdated: 'Status Updated',
      state: 'Status',
    };

    const data: Partial<NewOrder>[] = orders.map((order) => {
      const totalPaid = getTotalPaid(order);
      const outstanding = getOutstanding(order);
      const planValue = calcPlanValue(order.plans ? order.plans : []);

      return {
        customerName: getBenefactor(order)?.name ?? 'N/A',
        name: order.name,
        reference: order.reference,
        dateCreated: displayDate(order.createdAt),
        expireDate: displayDateTime(order.expire),
        planValue: toRandString(planValue),
        totalReceived: toRandString(totalPaid),
        outstanding: toRandString(outstanding),
        finalPayment: displayDate(order.endDateMax),
        dateUpdated: displayDate(order.stateUpdatedAt),
        state: order.state,
      };
    });

    const url = generateCsvBlobUrl(headerMap, data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `orders-${dayjs().format('YYYY-MM-DD')}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  } finally {
    isDownloadingCsv.value = false;
  }
};
// Map column accessorKeys to API field names
const columnToApiFieldMap: Record<string, string> = {
  dateCreated: 'createdAt',
  finalPayment: 'endDateMax',
  planValue: 'amountDue',
  outstanding: 'balance',
};
const onSortingChange = (sortingState: Array<{ id: string; desc: boolean }>) => {
  if (sortingState.length === 0) {
    delete orderParams.value.sort;
  } else {
    const { id, desc } = sortingState[0]; // TanStack table only supports single column sorting by default
    const apiField = columnToApiFieldMap[id] || id;
    orderParams.value.sort = desc ? `-${apiField}` : apiField;
  }
  pageNumber.value = 1; // Reset to first page when sorting changes
  onUpdateFilter(orderParams.value);
};
</script>
